import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react'
import { compact, omit } from 'lodash'
import { useTranslation, withI18nContext } from '@mc/i18n'
import { Button, ButtonProps, renderChildren } from '../../components'
import i18nInstance from '../../i18n/i18nInstance'
import {
  useScroller,
  getHorizontalPadding,
  getInnerWidth,
  getScrollWidth,
  getInlineSnapAlign,
} from './scrolling'
import { useDebouncedCallback } from './useDebouncedCallback'
import styles from './Carousel.module.scss'

// ignoring this lint error, since using `HTMLElement` _as a type_ should be fine for SSR
type Container = HTMLElement

type CarouselContextType = {
  srLabel: string
  position: number
  initialPage: number
  setPosition: React.Dispatch<React.SetStateAction<number>>
  page: number
  scrollCount: number
  loops: boolean
  tileCount: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  nextPage: () => void
  prevPage: () => void
  pages: number
  setPages: React.Dispatch<React.SetStateAction<number>>
  start: boolean
  setStart: React.Dispatch<React.SetStateAction<boolean>>
  end: boolean
  setEnd: React.Dispatch<React.SetStateAction<boolean>>
  container: Container | null
  setContainer: React.Dispatch<React.SetStateAction<Container | null>>
  firstVisibleIndex: () => number
}

type WithChild = {
  children?: React.ReactNode
}

type WithChildren = {
  children?: React.ReactNode | React.ReactNode[]
}

type As = React.ElementType | React.ComponentType

type WithAs = {
  as?: As
}

const CarouselContext = createContext<CarouselContextType | null>(null)

export const useCarousel = () => {
  const ctx = useContext(CarouselContext)
  if (!ctx) {
    throw new Error('Cannot use CarouselContext outside of provider')
  }
  return ctx
}

// event callbacks should always be allowed to be undefined
type OnEventCallback = (() => void) | undefined

export type CarouselProps = {
  srLabel: string // required for a11y, describes the content within the carousel
  initialPage?: number
  onClickNext?: OnEventCallback
  onClickPrev?: OnEventCallback
  /**
   * Whether to loop the carousel infinitely
   */
  loops?: boolean
  /**
   * Number of children by which to advance the carousel via next/prev buttons
   *
   * 0 or undefined indicates a "full page" according to current screen width
   */
  scrollCount?: number
} & WithChildren

export const Carousel = ({
  srLabel = 'carousel',
  initialPage = 0,
  children,
  onClickNext,
  onClickPrev,
  loops = false,
  // scrollCount = 0 indicates a "full page" according to current screen width
  scrollCount = 0,
}: CarouselProps) => {
  const [position, setPosition] = useState(0)
  const [page, setPage] = useState(0)
  const [pages, setPages] = useState(0)
  const [start, setStart] = useState(true)
  const [end, setEnd] = useState(false)
  const [container, setContainer] = useState<Container | null>(null)

  const paginator = useScroller(loops, container, scrollCount)

  const nextPage = () => {
    onClickNext?.()

    paginator.forward()
  }

  const prevPage = () => {
    onClickPrev?.()

    paginator.backward()
  }

  return (
    <CarouselContext.Provider
      value={{
        srLabel,
        tileCount: paginator.tileCount,
        position,
        initialPage,
        setPosition,
        loops,
        scrollCount,
        page,
        setPage,
        nextPage,
        prevPage,
        pages,
        setPages,
        start,
        setStart,
        end,
        setEnd,
        container,
        setContainer,
        firstVisibleIndex: paginator.firstVisibleIndex,
      }}
    >
      <div>{children}</div>
    </CarouselContext.Provider>
  )
}

type CarouselContentProps = {
  className?: string
  overflow?: boolean
  onPositionChanged?: (direction: 'right' | 'left') => void
  onItemChanged?: (index: number) => void
  scrollSnapAlign?: 'start' | 'center' | 'end' | 'none'
  /**
   * Scale factor applied to non-focused (non-centered) items, e.g. 0.9.
   * Only takes effect when scrollSnapAlign is 'center'.
   */
  focusScale?: number
  /**
   * Opacity applied to non-focused (non-centered) items, e.g. 0.6.
   * Only takes effect when scrollSnapAlign is 'center'.
   */
  focusOpacity?: number
  style?: React.CSSProperties
} & WithChildren &
  WithAs

// scrolling happens continuously at 16ms intervals
// this should be high enough to prevent calling during the animation,
// but low enough to be responsive
const DEBOUNCE = 50

export const CarouselContent = ({
  as: Component = 'section',
  children,
  className = '',
  overflow = false,
  onPositionChanged,
  onItemChanged,
  scrollSnapAlign = 'start',
  focusScale,
  focusOpacity,
  ...restProps
}: CarouselContentProps) => {
  const {
    srLabel: label,
    position,
    initialPage,
    scrollCount,
    tileCount,
    setPosition,
    setPage,
    setPages,
    setStart,
    setEnd,
    container,
    setContainer,
    firstVisibleIndex,
    loops,
  } = useCarousel()

  const delayedPositionChanged = useDebouncedCallback(() => {
    if (!container) {
      return
    }

    const padding = getHorizontalPadding(container)
    const scrollWidth = container.scrollWidth - padding

    const pageWidth = container.clientWidth - padding

    const pagesByWidth = scrollWidth / pageWidth

    // When scrolling right `left` prop in scroll needs to be at least the page width.
    // But ideally we would want to trigger the loop before that so there is less chance
    // that the user will see the loop end before it transitions.
    //
    // TOO FEW PAGES CASE
    // If pagesByWidth / 2 < 2 it means that there are less than 2 full pages in the container.
    // There could be one and a half pages for example. And in that case the rightOffset can't
    // be more than `scrollWidth - pageWidth` because if it was then the loop would never trigger.
    // For that case we also need to have a special leftOffset of the size of half a page to
    // ensure that the trigger zones don't overlap and start a transition loop of their own.
    //
    // 2 OR MORE FULL PAGES CASE
    // For all other cases we want to maintain the user mostly in the middle zone of the sroll.
    // If they reach further than 3/4ths of the scrollWidth then we trigger the loop back
    // If they reach less than 1/8th of the scrollWidth then we trigger the loop forward,
    // it is 1/8th instead of 1/4th because we want to avoid the overlap with the rightOffset.

    const tooFewPages = pagesByWidth / 2 < 2
    const rightOffset = tooFewPages
      ? scrollWidth - pageWidth
      : (scrollWidth * 3) / 4
    const leftOffset = tooFewPages ? 0 : scrollWidth / 8

    if (loops && container.scrollLeft >= rightOffset) {
      container.scroll({
        left: container.scrollLeft - scrollWidth / 2,
        behavior: 'instant',
      })
      // When scrolling left we don't have that issue, and it is better to put a lower number
      // to the condition so these two conditionals don't start a loop of their own.
    } else if (loops && container.scrollLeft < leftOffset) {
      container.scroll({
        left: container.scrollLeft + scrollWidth / 2,
        behavior: 'instant',
      })
    }

    onPositionChanged?.(
      position > (container?.scrollLeft ?? 0) ? 'left' : 'right',
    )
  }, DEBOUNCE)

  const delayedItemChanged = useDebouncedCallback(() => {
    if (!container) return
    const idx = firstVisibleIndex()

    onItemChanged?.(idx)
  }, DEBOUNCE)

  // When scroll-snap is center-aligned, the first and last items can't
  // reach the viewport center on their own (scrollLeft can't go negative
  // for the first, and is clamped at max for the last). This effect adds
  // inline margins to create the extra scroll space they need.
  useLayoutEffect(() => {
    // Only relevant for center-aligned carousels
    if (!container || scrollSnapAlign !== 'center') return undefined

    const items = Array.from(container.children) as HTMLElement[]
    if (items.length === 0) return undefined

    const first = items[0]
    const last = items[items.length - 1]

    const updateCenterMargins = () => {
      // Respect effective CSS override on children (e.g. mobile
      // `scroll-snap-align: start !important`).
      if (getInlineSnapAlign(first) !== 'center') {
        first.style.marginInlineStart = ''
        if (last !== first) last.style.marginInlineEnd = ''
        return
      }

      // Reset margins before measuring so offsetWidth reflects the
      // true tile size without previous margin interference
      first.style.marginInlineStart = '0'
      if (last !== first) last.style.marginInlineEnd = '0'

      // Visible width of the scroll container minus its CSS padding
      const padding = getHorizontalPadding(container)
      const viewport = container.clientWidth - padding

      // Space needed on each side so the tile's center aligns with
      // the viewport's center: (viewport - tile) / 2
      const margin = Math.max(0, (viewport - first.offsetWidth) / 2)

      first.style.marginInlineStart = `${margin}px`
      if (last !== first) {
        const lastMargin = Math.max(0, (viewport - last.offsetWidth) / 2)
        last.style.marginInlineEnd = `${lastMargin}px`
      }
    }

    updateCenterMargins()

    // Track width so we skip resize events that only change height
    // (e.g. iOS Safari browser bar show/hide), which would otherwise
    // cause a margin-recalc → scroll → focusScale feedback loop.
    let lastWidth = container.clientWidth
    let raf: number
    const onResize = () => {
      const currentWidth = container.clientWidth
      if (currentWidth === lastWidth) return
      lastWidth = currentWidth
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(updateCenterMargins)
    }
    window.addEventListener('resize', onResize)

    return () => {
      cancelAnimationFrame(raf)
      first.style.marginInlineStart = ''
      if (last !== first) last.style.marginInlineEnd = ''
      window.removeEventListener('resize', onResize)
    }
  }, [container, scrollSnapAlign, children])

  useLayoutEffect(() => {
    const hasFocusEffect = focusScale != null || focusOpacity != null
    if (!container || !hasFocusEffect || scrollSnapAlign !== 'center')
      return undefined

    const resetItem = (child: HTMLElement) => {
      child.style.transform = ''
      child.style.opacity = ''
    }

    let raf: number

    const updateFocused = () => {
      const items = Array.from(container.children) as HTMLElement[]
      if (items.length === 0) return

      // If a consumer overrides snap alignment to start/end with CSS at a
      // breakpoint, disable the center-only focus effects for that mode.
      if (getInlineSnapAlign(items[0]) !== 'center') {
        items.forEach(resetItem)
        return
      }

      const containerCenter = container.scrollLeft + container.clientWidth / 2
      let closestIdx = 0
      let closestDist = Infinity

      items.forEach((child, i) => {
        const childCenter = child.offsetLeft + child.offsetWidth / 2
        const dist = Math.abs(childCenter - containerCenter)
        if (dist < closestDist) {
          closestDist = dist
          closestIdx = i
        }
      })

      items.forEach((child, i) => {
        const isFocused = i === closestIdx
        if (focusScale != null) {
          child.style.transform = isFocused
            ? 'scale(1)'
            : `scale(${focusScale})`
        }
        if (focusOpacity != null) {
          child.style.opacity = isFocused ? '1' : String(focusOpacity)
        }
      })
    }

    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(updateFocused)
    }

    updateFocused()
    container.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)

    return () => {
      cancelAnimationFrame(raf)
      container.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      Array.from(container.children).forEach((child) => {
        resetItem(child as HTMLElement)
      })
    }
  }, [container, focusScale, focusOpacity, scrollSnapAlign, children])

  useEffect(() => {
    const setProperties = () => {
      if (container) {
        const idx = firstVisibleIndex()

        // we don't want to include overflow tiles into the page calculations
        const padding = getHorizontalPadding(container)
        const innerWidth = container.clientWidth - padding
        const scrollWidth = container.scrollWidth - padding

        // calculate tile width and tilesPerPage since tiles can have fixed width
        // that doesn't change with screen size and there might be partial tiles visible
        const tileWidth = scrollWidth / tileCount
        const tilesPerPage = Math.floor(innerWidth / tileWidth) || 1

        // ceil to give correct whole number rounding when page calc is not exact
        const pages =
          scrollCount > 0
            ? Math.ceil(tileCount / scrollCount)
            : Math.ceil(tileCount / tilesPerPage)
        const page =
          scrollCount > 0
            ? Math.floor(idx / scrollCount)
            : Math.ceil(idx / tilesPerPage)

        setPage(page)
        setPages(pages)
        setStart(position === 0)

        const currentEnd = Math.ceil(position + innerWidth)
        setEnd(currentEnd >= scrollWidth)

        delayedItemChanged()
      }
    }

    setProperties()

    let lastWidth = container?.clientWidth ?? 0
    let raf: number
    const onResize = () => {
      const currentWidth = container?.clientWidth ?? 0
      if (currentWidth === lastWidth) return
      lastWidth = currentWidth
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(setProperties)
    }
    window.addEventListener('resize', onResize)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)
    }
  }, [
    children,
    container,
    position,
    scrollCount,
    setPage,
    setPages,
    setEnd,
    setStart,
    onItemChanged,
    delayedItemChanged,
    firstVisibleIndex,
    tileCount,
    loops,
  ])

  useEffect(() => {
    if (container?.scroll) {
      const innerWidth = getInnerWidth(container)
      const scrollWidth = getScrollWidth(container)

      if (loops) {
        container.scroll({
          left: scrollWidth / 2,
          behavior: 'instant',
        })
      }
      if (initialPage > 0) {
        container.scroll({
          left: initialPage * innerWidth,
          behavior: 'auto',
        })
      }
    }
  }, [container, initialPage, loops, tileCount])

  useEffect(() => {
    if (container) {
      Array.from(container.children).forEach((child, i) => {
        child.setAttribute('role', 'group')

        const descriptiveLabel = child.getAttribute('aria-label')
          ? `${child.getAttribute('aria-label')}, `
          : ''

        child.setAttribute(
          'aria-label',
          `${descriptiveLabel}${
            (i % tileCount) + 1
          } of ${tileCount} in ${label}`,
        )
      })
    }
  }, [container, label, tileCount])

  const handleScroll: React.EventHandler<React.UIEvent<Container>> = () => {
    if (!container) {
      return
    }
    setPosition(container.scrollLeft)
    delayedPositionChanged()
  }

  return (
    <Component
      aria-label={label}
      {...restProps}
      className={compact([
        styles.content,
        className,
        scrollSnapAlign && scrollSnapAlign !== 'none'
          ? styles[
              `scrollSnapAlign${
                scrollSnapAlign.charAt(0).toUpperCase() +
                scrollSnapAlign.slice(1)
              }`
            ]
          : '',
        overflow ? styles.overflow : '',
        focusScale != null || focusOpacity != null ? styles.hasFocusEffect : '',
      ]).join(' ')}
      onScroll={handleScroll}
      ref={setContainer}
    >
      {children}
    </Component>
  )
}

export const CarouselItem = ({ children }: WithChild) => {
  const { container } = useCarousel()
  const childRef = useRef<HTMLDivElement>(null)
  const [isIntersecting, setIsIntersecting] = useState(false)

  useEffect(() => {
    if (container && childRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setIsIntersecting(entry.isIntersecting)
          })
        },
        { root: container },
      )

      observer.observe(childRef.current)

      return () => {
        observer.disconnect()
      }
    }

    return () => {}
  }, [container, childRef])

  return (
    <>
      {renderChildren(children, {
        ref: childRef,
        tabIndex: isIntersecting ? undefined : -1,
        'aria-hidden': !isIntersecting,
      })}
    </>
  )
}

export const CarouselInteractive = ({ children }: WithChild) => {
  const { container } = useCarousel()
  const childRef = useRef<HTMLDivElement>(null)
  const [isIntersecting, setIsIntersecting] = useState(false)

  useEffect(() => {
    if (container && childRef.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setIsIntersecting(entry.isIntersecting)
          })
        },
        { root: container },
      )

      observer.observe(childRef.current)
    }
  }, [container, childRef])

  return (
    <>
      {renderChildren(children, {
        ref: childRef,
        tabIndex: isIntersecting ? 0 : -1,
        'aria-hidden': !isIntersecting,
      })}
    </>
  )
}

export const CarouselArrows = ({ children }: WithChildren) => (
  <div className={styles.arrowsClassic}>{children}</div>
)

export const CarouselPrevious = withI18nContext(i18nInstance)((
  props: ButtonProps,
) => {
  const { t } = useTranslation('@mc/design-system')

  const { loops, pages, prevPage, start } = useCarousel()
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    props.onClick?.(e)
    prevPage()
  }

  if (pages < 2 && !props.alwaysShowControls) return <></>

  const disabled = start && !loops

  return (
    <Button
      {...omit(props, 'alwaysShowControls')}
      className={[
        props.className,
        styles.arrow,
        styles.prev,
        disabled ? styles.disabled : '',
      ]
        .join(' ')
        .trim()}
      onClick={handleClick}
      disabled={disabled}
      aria-label={t('carousel.previousButtonAlt')}
    />
  )
})

export const CarouselNext = withI18nContext(i18nInstance)((
  props: ButtonProps,
) => {
  const { t } = useTranslation('@mc/design-system')

  const { loops, pages, end, nextPage } = useCarousel()
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    props.onClick?.(e)
    nextPage()
  }

  if (pages < 2 && !props.alwaysShowControls) return <></>

  const disabled = end && !loops

  return (
    <Button
      {...omit(props, 'alwaysShowControls')}
      className={[
        props.className,
        styles.arrow,
        styles.next,
        disabled ? styles.disabled : '',
      ]
        .join(' ')
        .trim()}
      onClick={handleClick}
      disabled={disabled}
      aria-label={t('carousel.nextButtonAlt')}
    />
  )
})

export const CarouselPages = ({
  className = '',
  alwaysShowControls,
}: {
  className?: string
  alwaysShowControls?: boolean
}) => {
  const { pages, page, container, loops } = useCarousel()

  const actualPages = loops ? pages / 2 : pages

  const isActive = (i: number) => page % actualPages === i
  const handleClick = (i: number) => () =>
    container?.scroll({
      left: i * container.clientWidth,
      behavior: 'smooth',
    })

  if (pages < 2 && !alwaysShowControls) return <></>

  return (
    <div className={`${styles.pages} ${className}`}>
      {Array.from({ length: actualPages }).map((_, i) => (
        <button
          key={i}
          className={[styles.page, isActive(i) ? styles.active : ''].join(' ')}
          onClick={handleClick(i)}
          tabIndex={0}
          aria-current={isActive(i)}
          aria-label={`Page ${i + 1} of ${actualPages}`}
        ></button>
      ))}
    </div>
  )
}
